import type { DeviceForm } from './forms';

const STORAGE_KEY_PREFIX = 'upapp_device_forms_';

function storageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

function readAll(userId: string): DeviceForm[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    return raw ? (JSON.parse(raw) as DeviceForm[]) : [];
  } catch {
    console.warn('[deviceForms] Failed to parse localStorage');
    return [];
  }
}

function writeAll(userId: string, forms: DeviceForm[]): void {
  localStorage.setItem(storageKey(userId), JSON.stringify(forms));
}

export function saveDeviceForm(
  userId: string,
  formType: DeviceForm['formType'],
  formData: Record<string, any>,
  completionStatus: DeviceForm['completionStatus'] = 'draft',
  title = ''
): DeviceForm {
  const forms = readAll(userId);
  const now = new Date().toISOString();
  const newForm: DeviceForm = {
    id: crypto.randomUUID(),
    userId,
    formType,
    formData,
    completionStatus,
    title,
    aiFeedback: null,
    storageLocation: 'device',
    createdAt: now,
    updatedAt: now,
  };
  forms.push(newForm);
  writeAll(userId, forms);
  return newForm;
}

export function updateDeviceForm(
  userId: string,
  id: string,
  updates: Partial<Pick<DeviceForm, 'formData' | 'completionStatus' | 'title'>>
): DeviceForm {
  const forms = readAll(userId);
  const index = forms.findIndex((f) => f.id === id);
  if (index === -1) throw new Error(`Device form ${id} not found`);
  forms[index] = { ...forms[index], ...updates, updatedAt: new Date().toISOString() };
  writeAll(userId, forms);
  return forms[index];
}

export function listDeviceForms(userId: string): DeviceForm[] {
  return readAll(userId);
}

export function getDeviceForm(userId: string, id: string): DeviceForm | null {
  return readAll(userId).find((f) => f.id === id) ?? null;
}

export function deleteDeviceForm(userId: string, id: string): void {
  const forms = readAll(userId).filter((f) => f.id !== id);
  writeAll(userId, forms);
}
